import type { Schemas } from '@npc/api-client'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { ArrowLeft } from 'lucide-react'

import { t } from '../../copy'
import { T } from '../../copy/text'
import {
  useConfirmMemoryCandidate,
  useDiscardMemoryCandidate,
  useMemoryCandidates,
} from '../../app/queries'

/**
 * 「Lulu 建议记录」候选块（设计纠错清单 W14）。
 *
 * # 它是什么、不是什么
 *
 * 候选表里 pending 的那几条：Lulu 觉得值得记下来的话，**他不点保存就不存在**。
 * 文案模板（memo.candidates.*）2026-08 就写好了，这一块此前一个消费方都没有——
 * 于是候选表零行、零写入方、零读法，三处空着互相证明「没人用」。
 *
 * # 放哪儿（2026-09-09 用户定的）
 *
 * 不长在备忘录页顶上——那页只放他自己记的东西。候选单独一个页签
 * （`/memos/lulu`，`LuluRecordsScreen`），从备忘录页头进。
 *
 * # 四态
 *
 *   - 还在读 / 读不到：整块不画。这一块不是页面的主体，读不到不该挡备忘录本身
 *   - 一条都没有：整块不画（「沉默是好消息」）；空态那句留给将来的入口
 *   - 有：列出来，每条能保存或丢弃；决定了就从这里消失
 */
export function MemoryCandidatesBlock(props: { showHeader?: boolean } = {}) {
  const candidates = useMemoryCandidates('pending')
  const confirm = useConfirmMemoryCandidate()
  const discard = useDiscardMemoryCandidate()
  const [failed, setFailed] = useState<string | null>(null)
  const items = candidates.data?.items ?? []
  if (items.length === 0) return null
  return (
    <section data-testid="memo-candidates" className="mb-[16px]">
      {props.showHeader === false ? null : (
        <div className="mb-[8px] flex items-baseline gap-[8px]">
          <h3 className="m-0 text-npc-card-title font-semibold text-m-t1">
            <T id="memo.candidates.heading" />
          </h3>
          <span className="rounded-npc-chip bg-m-bg2 px-[6px] text-npc-meta text-m-t3">
            <T id="memo.candidates.badge" />
          </span>
        </div>
      )}
      {props.showHeader === false ? null : (
        <p className="mb-[8px] mt-0 text-npc-meta text-m-t3">
          <T id="memo.candidates.desc" />
        </p>
      )}
      <ul className="m-0 list-none overflow-hidden rounded-npc-card border border-m-line2 bg-m-bg1 p-0">
        {items.map((c) => (
          <CandidateRow
            key={c.id}
            candidate={c}
            busy={confirm.isPending || discard.isPending}
            failed={failed === c.id}
            onConfirm={async (onlyMe) => {
              setFailed(null)
              try {
                // 默认按 Lulu 提议的那一档（它提的默认给它看）；勾了「只给我看」就存成不给 AI 看。
                await confirm.mutateAsync({ candidateId: c.id, aiAccess: onlyMe ? 'hidden' : c.proposed_access })
              } catch {
                setFailed(c.id)
              }
            }}
            onDiscard={async () => {
              setFailed(null)
              try {
                // 这条 hook 收的是裸 id。2026-09-05 生产上传了个对象，路径变成
                // /memory-candidates/candidateId,<id>/discard → 400；msw 的 :id 照样匹配，用例没拦住。
                await discard.mutateAsync(c.id)
              } catch {
                setFailed(c.id)
              }
            }}
          />
        ))}
      </ul>
    </section>
  )
}

/**
 * 「Lulu 建议记录」独立页签（`/memos/lulu`）。
 *
 * 备忘录页只放用户自己记的内容；Lulu 攒的候选到这儿来决定存不存。
 * 独立页签里这一块就是页面主体，所以空态不再是「沉默」——
 * `memo.candidates.empty` 那句一直备着，就等这个入口。
 */
export function LuluRecordsScreen() {
  const candidates = useMemoryCandidates('pending')
  const items = candidates.data?.items ?? []

  return (
    <div className="flex flex-1 flex-col bg-m-bg0 text-m-t1">
      <header className="sticky top-0 z-20 border-b border-m-line1 bg-m-bg0 px-[16px] pb-[12px] pt-[14px] max-[700px]:px-npc-grid-sm">
        <div className="mx-auto flex w-full max-w-[1440px] items-end gap-[12px]">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-[9px]">
              <h1 className="m-0 truncate font-npc-display text-[26px] font-semibold tracking-npc-tight text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.candidates.heading" />
              </h1>
              <span
                data-testid="memo-lulu-badge"
                className="inline-flex shrink-0 items-center rounded-[5px] bg-m-bg4 px-[8px] py-[2px] text-[11px] text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
              >
                <T id="memo.candidates.badge" />
              </span>
            </div>
            <div className="mt-[4px] text-[13px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.candidates.desc" />
            </div>
          </div>
          <Link
            to="/memos"
            data-testid="memo-lulu-back"
            className="inline-flex h-[32px] shrink-0 items-center gap-[5px] rounded-[7px] border border-m-line2 bg-m-bg1 px-[13px] text-[12.5px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            <ArrowLeft size={13} strokeWidth={1.8} aria-hidden />
            <T id="memo.page.back.list" />
          </Link>
        </div>
      </header>

      <div className="px-[16px] pb-[48px] pt-[14px] max-[700px]:px-npc-grid-sm">
        <div className="mx-auto w-full max-w-[1440px]">
          {candidates.isPending ? (
            <p className="m-0 text-[12px] text-m-t3" data-testid="memo-candidates-loading"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.loading" />
            </p>
          ) : items.length === 0 ? (
            <div
              data-testid="memo-candidates-empty"
              className="rounded-npc-card border border-dashed border-m-line2 bg-m-bg1 px-[18px] py-[26px] text-center"
            >
              <p className="mx-auto mb-0 mt-[6px] max-w-[520px] text-[15px] leading-[1.6] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.candidates.empty" />
              </p>
            </div>
          ) : (
            // 页头已带标题/徽标/说明，区块只画清单本身
            <MemoryCandidatesBlock showHeader={false} />
          )}
        </div>
      </div>
    </div>
  )
}

function CandidateRow({
  candidate,
  busy,
  failed,
  onConfirm,
  onDiscard,
}: {
  readonly candidate: Schemas['MemoMemoryCandidate']
  readonly busy: boolean
  readonly failed: boolean
  readonly onConfirm: (onlyMe: boolean) => Promise<void>
  readonly onDiscard: () => Promise<void>
}) {
  const [onlyMe, setOnlyMe] = useState(false)
  return (
    <li
      data-testid="memo-candidate"
      data-candidate-id={candidate.id}
      className="border-b border-m-line1 px-[14px] py-[12px] last:border-b-0"
    >
      <p data-testid="memo-candidate-summary" className="m-0 text-npc-body text-m-t1">
        {candidate.summary}
      </p>
      <p className="mb-0 mt-[4px] text-npc-meta text-m-t3">
        <span className="mr-[6px]">
          <T id="memo.candidates.why" />
        </span>
        {candidate.why_keep}
      </p>
      {candidate.source_conversation_id ? (
        <p className="mb-0 mt-[2px] text-npc-meta text-m-t3">
          <span className="mr-[6px]">
            <T id="memo.candidates.source" />
          </span>
          <Link
            data-testid="memo-candidate-source"
            to="/lulu/c/$conversationId"
            params={{ conversationId: candidate.source_conversation_id }}
            className="underline"
          >
            {candidate.source_note && candidate.source_note !== '' ? candidate.source_note : t('memo.candidates.source')}
          </Link>
        </p>
      ) : candidate.source_note && candidate.source_note !== '' ? (
        <p className="mb-0 mt-[2px] text-npc-meta text-m-t3">
          <span className="mr-[6px]">
            <T id="memo.candidates.source" />
          </span>
          {candidate.source_note}
        </p>
      ) : null}
      <label className="mt-[8px] flex items-center gap-[6px] text-npc-meta text-m-t2">
        <input
          type="checkbox"
          data-testid="memo-candidate-only-me"
          checked={onlyMe}
          onChange={(e) => setOnlyMe(e.target.checked)}
        />
        <T id="memo.candidates.only_me" />
      </label>
      <div className="mt-[8px] flex items-center gap-[8px]">
        <button
          type="button"
          data-testid="memo-candidate-confirm"
          disabled={busy}
          onClick={() => void onConfirm(onlyMe)}
          className="rounded-full bg-m-pri px-[10px] py-[4px] text-npc-caption font-semibold text-m-pri-t disabled:opacity-50"
        >
          <T id="memo.candidates.confirm" />
        </button>
        <button
          type="button"
          data-testid="memo-candidate-discard"
          disabled={busy}
          onClick={() => void onDiscard()}
          className="rounded-npc-button border border-m-line2 px-[10px] py-[4px] text-npc-caption text-m-t2 disabled:opacity-50"
        >
          <T id="memo.candidates.discard" />
        </button>
        {failed ? (
          <span data-testid="memo-candidate-failed" className="text-npc-meta text-npc-red">
            <T id="error.generic" />
          </span>
        ) : null}
      </div>
    </li>
  )
}
